export interface TranslationSet {
  // Navigation & General UI
  langToggle: string;
  langLabel: string;
  projectName: string;
  projectSubtitle: string;
  exploreBtn: string;
  archetypeBtn: string;
  techBtn: string;
  pleiadianArchiveBtn: string;
  lyraChroniclesBtn: string;
  backToMain: string;
  readMore: string;
  close: string;
  activate: string;
  playing: string;
  mute: string;

  // Hero Section
  heroTitle: string;
  heroSub: string;

  // Section 1: Who are the Pleiadians?
  sec1Title: string;
  sec1Intro: string;
  sec1Sub1Title: string;
  sec1Sub1Text: string;
  sec1Sub2Title: string;
  sec1Sub2Text: string;
  sec1Sub3Title: string;
  sec1Sub3Text: string;
  sec1Sub4Title: string;
  sec1Sub4Text: string;

  // Section 2: History through cultures
  sec2Title: string;
  sec2Intro: string;
  sec2Sub1Title: string;
  sec2Sub1Text: string;
  sec2Sub2Title: string;
  sec2Sub2Text: string;
  sec2Sub3Title: string;
  sec2Sub3Text: string;
  sec2Sub4Title: string;
  sec2Sub4Text: string;
  sec2Sub5Title: string;
  sec2Sub5Text: string;

  // Section 3: Pleiadian civilization
  sec3Title: string;
  sec3Intro: string;
  sec3Sub1Title: string;
  sec3Sub1Text: string;
  sec3Sub2Title: string;
  sec3Sub2Text: string;
  sec3Sub3Title: string;
  sec3Sub3Text: string;
  sec3Sub4Title: string;
  sec3Sub4Text: string;
  sec3Sub5Title: string;
  sec3Sub5Text: string;

  // Section 4: Technology
  sec4Title: string;
  sec4Intro: string;
  sec4Sub1Title: string;
  sec4Sub1Text: string;
  sec4Sub2Title: string;
  sec4Sub2Text: string;
  sec4Sub3Title: string;
  sec4Sub3Text: string;
  sec4Sub4Title: string;
  sec4Sub4Text: string;
  sec4Sub5Title: string;
  sec4Sub5Text: string;

  // Section 5: Pleiadians & Human Civilization
  sec5Title: string;
  sec5Intro: string;
  sec5Sub1Title: string;
  sec5Sub1Text: string;
  sec5Sub2Title: string;
  sec5Sub2Text: string;
  sec5Sub3Title: string;
  sec5Sub3Text: string;
  sec5Sub4Title: string;
  sec5Sub4Text: string;
  sec5Sub5Title: string;
  sec5Sub5Text: string;

  // Section 6: Comparative Cosmology
  sec6Title: string;
  sec6Intro: string;
  sec6Sub1Title: string;
  sec6Sub1Text: string;
  sec6Sub2Title: string;
  sec6Sub2Text: string;
  sec6Sub3Title: string;
  sec6Sub3Text: string;
  sec6Sub4Title: string;
  sec6Sub4Text: string;

  // Section 7: Evolutionary models
  sec7Title: string;
  sec7Intro: string;
  sec7Sub1Title: string;
  sec7Sub1Text: string;
  sec7Sub2Title: string;
  sec7Sub2Text: string;
  sec7Sub3Title: string;
  sec7Sub3Text: string;
  sec7Sub4Title: string;
  sec7Sub4Text: string;

  // Section 8: Interactive Resources
  sec8Title: string;
  sec8Intro: string;
  interactiveMapTitle: string;
  interactiveMapDesc: string;
  frequencySynthTitle: string;
  frequencySynthDesc: string;
  archetypeMapTitle: string;
  archetypeMapDesc: string;

  // Subpages
  subpage1Title: string;
  subpage1Full: string;
  subpage2Title: string;
  subpage2Full: string;
  subpage3Title: string;
  subpage3Full: string;
  subpage4Title: string;
  subpage4Full: string;
  subpage5Title: string;
  subpage5Full: string;
  subpage6Title: string;
  subpage6Full: string;
  subpage8Title: string;
  subpage8Full: string;
  subpage9Title: string;
  subpage9Full: string;
  subpage11Title: string;
  subpage11Full: string;
}

export const translations: Record<"en" | "hr", TranslationSet> = {
  en: {
    langToggle: "Hrvatski",
    langLabel: "Language",
    projectName: "Pleiades",
    projectSubtitle: "Star Archetype of Harmony",
    exploreBtn: "Explore Pleiades",
    archetypeBtn: "Star Archetype",
    techBtn: "Civilization & Technology",
    pleiadianArchiveBtn: "Pleiadian Archive",
    lyraChroniclesBtn: "Lyra Chronicles",
    backToMain: "Back to Main",
    readMore: "Read Deep Analysis",
    close: "Close",
    activate: "Activate Frequency",
    playing: "Emitting Resonance",
    mute: "Mute",

    // Hero
    heroTitle: "Pleiades — Archetype of Harmony, Consciousness, and Star Clan",
    heroSub: "In-depth cosmology, mythological history, evolutionary models, and metaphysical analysis of the Pleiadian civilization.",

    // Section 1
    sec1Title: "Who are the Pleiadians?",
    sec1Intro: "Pleiadians represent the archetype of cosmic coherence, galactic sisterhood, evolutionary harmony, and starlight intelligence.",
    sec1Sub1Title: "Astronomical Identity",
    sec1Sub1Text: "Known as Messier 45 (M45) or the Seven Sisters, this open cluster of hot, blue B-type stars resides in the constellation of Taurus. Approximately 444 light-years from Earth, it is one of the closest and most visually striking star clusters, bound together by gravity and glowing with an ethereal reflection nebula.",
    sec1Sub2Title: "Mythological Identity",
    sec1Sub2Text: "Across epochs, cultures recognized these stars. In Greek mythology, they are the daughters of Atlas and Pleione, chased by Orion. In ancient Egypt, they represented the pathways of Hathor. In Japan, they are Subaru ('coming together'), and in Australian Aboriginal lore, they are the Seven Sisters fleeing ancestral pursuers.",
    sec1Sub3Title: "Esoteric Identity",
    sec1Sub3Text: "Modern metaphysical models, theosophy, and channelling view the Pleiades as a high-frequency density system. They are described as a home of light beings who act as guardians and spiritual mentors for Earth's transition into higher dimensional densities.",
    sec1Sub4Title: "Why We Are Attracted",
    sec1Sub4Text: "Archetypal psychology suggests the Pleiades trigger a deep-seated cellular memory of peaceful coexistence, social harmony, and communal telepathy. They serve as a cosmic mirror for humanity's potential transition from competitive struggle into unified coherence.",

    // Section 2
    sec2Title: "History of the Pleiades Across Cultures",
    sec2Intro: "From prehistoric caves to modern metaphysics, the Pleiades are woven into the collective tapestry of Earth's ancestral memory.",
    sec2Sub1Title: "Prehistoric Myths of the Seven Sisters",
    sec2Sub1Text: "Dating back to the Upper Paleolithic, representations of the Pleiades are found in the Lascaux cave paintings (approx. 17,000 years old). Anthropologists note that almost all isolated global cultures associate them with 'seven sisters', suggesting a common ancestral story before continental dispersal.",
    sec2Sub2Title: "Ancient Civilizations",
    sec2Sub2Text: "In Mesopotamia, they were the Mul-Mul, symbolizing stars par excellence. In Mayan cosmology, the Pleiades (Tzab-ek or rattlesnake's tail) were considered the origin point of their sacred calendar, Tzolk'in. Ancient Indian Vedic scriptures call them the Krittikas, representing the sacred fire and light.",
    sec2Sub3Title: "Shamanic Traditions",
    sec2Sub3Text: "Indigenous shamans from North America (Cherokee, Lakota) to the Andes view the Pleiades as the celestial home of their ancestors. Ceremonies are timed with the Pleiades heliacal rising, marking agriculture, initiation rites, and portals of energetic cleansing.",
    sec2Sub4Title: "Modern Esotericism",
    sec2Sub4Text: "Early 20th-century movements (Theosophy, Alice Bailey, Rudolf Steiner) positioned the Pleiades, particularly Alcyone, as the 'Central Sun' of our stellar neighborhood, a transmission hub of Divine Will and evolutionary rays affecting human intelligence.",
    sec2Sub5Title: "Supratemporal Cosmology",
    sec2Sub5Text: "Contemporary metaphysics integrates quantum mechanics, treating the Pleiadian system as a multi-dimensional node where consciousness experiences density-shifts. It connects galactic history with Earth’s spiritual awakening.",

    // Section 3
    sec3Title: "Pleiadian Civilization (The Meta-Level)",
    sec3Intro: "Operating beyond physical boundaries, the Pleiadian model of civilization is anchored in collective heart coherence, empathy, and harmonic resonance.",
    sec3Sub1Title: "Social Structure: The Clan Network",
    sec3Sub1Text: "Unlike hierarchical command systems, Pleiadian society is organized as a dynamic, non-local network. Decentralized clans govern based on expertise, spiritual frequency, and mutual consent, achieving perfect coordination through telepathic synergy.",
    sec3Sub2Title: "Ethics & Philosophy",
    sec3Sub2Text: "Their core philosophy dictates that individual evolution is inseparable from collective well-being. Deep empathy is not just a virtue, but a literal biological and telepathic reality where harming another instantly register as self-harm.",
    sec3Sub3Title: "Evolutionary Model: Coherence & Mentorship",
    sec3Sub3Text: "Growth occurs through concentric circles of coherence. Rather than conquering, they expand by mentoring younger species. Once a civilization achieves self-sustainability, they step back to preserve local autonomy.",
    sec3Sub4Title: "The Seven Archetypes of Consciousness",
    sec3Sub4Text: "Represented by the seven stars, each represents a distinct archetype: Unity (Alcyone), Bioresonance (Maia), Inner Alchemy (Merope), Sonic Architecture (Taygeta), Quantum Network (Electra), Oracle Insight (Celaeno), and Structural Balance (Asterope).",
    sec3Sub5Title: "Role in Galactic Narratives",
    sec3Sub5Text: "Known as the 'Cosmic Peacemakers', they act as mediators in planetary conflicts, spiritual counselors, and cosmic healers. They assist in harmonizing unstable sectors of the Milky Way.",

    // Section 4
    sec4Title: "Pleiadian Technology",
    sec4Intro: "A scientific-metaphysical synthesis where technology is organic, resonant, and operated directly by high-vibrational consciousness.",
    sec4Sub1Title: "Frequency Technology",
    sec4Sub1Text: "Matter is manipulated using sound, light, and scalar waves. Objects are not 'manufactured' but sung or vibrated into existence by aligning geometric frequencies with the local quantum field.",
    sec4Sub2Title: "Network Intelligence",
    sec4Sub2Text: "Their interfaces are direct neural and telepathic extensions. Computers are biological crystalline grids that respond to clear intention and emotional coherence, preventing destructive uses.",
    sec4Sub3Title: "Organic & Bioresonant Tech",
    sec4Sub3Text: "All structures are alive and self-healing. Spacecraft are grown as conscious, bio-plasmic entities that possess a symbiotic bond with their pilots, navigating through dimensional folding.",
    sec4Sub4Title: "Light Architecture",
    sec4Sub4Text: "Cities are constructed of liquid light and crystalline materials. These structures act as giant lenses, focusing cosmic rays and terrestrial telluric currents to create high-frequency sanctuaries.",
    sec4Sub5Title: "Comparison with Earth's Tech",
    sec4Sub5Text: "Where humanity uses brute force (electricity, combustion, binary code), Pleiadian tech is gentle. It parallels our developing fields of Quantum Computing, AI Neural Networks, and Biocomputers, but operates at a fully integrated spiritual level.",

    // Section 5
    sec5Title: "Pleiadians and Human Civilization",
    sec5Intro: "An exploration of why human hearts consistently resonate with the Pleiades, tracing our shared spiritual lineage and evolutionary goals.",
    sec5Sub1Title: "Archetypal Influences",
    sec5Sub1Text: "Pleiadian codes are deeply embedded in human art, music, and architecture. The pursuit of geometry, peaceful cohabitation, and beautiful design are cultural extensions of this cosmic archetype.",
    sec5Sub2Title: "Motifs in Myths & Religions",
    sec5Sub2Text: "In Native American medicine wheels, ancient stone circles, and Eastern temples, alignments to the Pleiades are ubiquitous. They represent a portal through which higher spiritual laws were delivered to Earth.",
    sec5Sub3Title: "The Resonance of Recognition",
    sec5Sub3Text: "Many people feel a home-sickness when gazing at M45. This psychological phenomenon is often interpreted as a soul-level recognition of a peaceful state of existence that we are meant to rebuild on Earth.",
    sec5Sub4Title: "The Evolutionary Ideal",
    sec5Sub4Text: "The Pleiades represent a preview of humanity's future. They are our mirror, showing that a civilization can transcend greed, war, and separation to live as conscious caretakers of their biosphere.",
    sec5Sub5Title: "Psychological & Spiritual Magnetism",
    sec5Sub5Text: "It appeals to our desire for safety, unconditional love, and spiritual maturity. The archetype guides the human ego from the survival-based root chakra into the cooperative, heart-centered heart chakra.",

    // Section 6
    sec6Title: "The Lyra–Orion Catalyst: Galactic Turning Point",
    sec6Intro: "A cosmic threshold beyond imagination — unveiling the ancient confrontation that reshaped the future of the entire Galaxy.",
    sec6Sub1Title: "Pleiades vs. Sirius",
    sec6Sub1Text: "Pleiadians emphasize fluid empathy, relational harmony, and the feminine heart-centered path. Sirians represent sacred geometry, dense structures, portals, legal-cosmic frameworks, and the masculine mind-centered path of organization.",
    sec6Sub2Title: "Pleiades vs. Atlantis",
    sec6Sub2Text: "Atlantis (at its peak and eventual fall) represents technological power, mechanical manipulation, and hierarchical control. Pleiadians choose ecological synthesis, soft technologies, and decentralized non-violent coherence.",
    sec6Sub3Title: "Pleiades vs. Lyra",
    sec6Sub3Text: "Lyrans represent the raw, fiery, creative genesis of humanoid life—vibrant, physical, and aesthetic. Pleiadians are the distilled, refined, and harmonized evolution of that seed, operating in fluid unity.",
    sec6Sub4Title: "The Cosmic Harmonizers",
    sec6Sub4Text: "Among galactics, Pleiadians act as the buffer and diplomatic balm. Their soft energy bridges the intense structural drive of Sirius, the wild exploration of Lyra, and the complex shadows of Orion.",

    // Section 7
    sec7Title: "Evolutionary Models and Metaphysics",
    sec7Intro: "The philosophical pillars of conscious ascension, explaining how individual souls evolve into collective galactic awareness.",
    sec7Sub1Title: "Model of Ascension",
    sec7Sub1Text: "Evolution is measured by the degree of integrated unity. The transition goes from 3D (physical separation) to 4D (astral exploration) to 5D (heart coherence and telepathy) and 6D (light-body networks).",
    sec7Sub2Title: "Collective Coherence",
    sec7Sub2Text: "True power is not coercive but resonant. When a group aligns their emotional states (heart rate variability and intent), they form a collective mind-grid capable of healing matter and altering time lines.",
    sec7Sub3Title: "Duhovna Psihologija",
    sec7Sub3Text: "Spiritual psychology in the Pleiadian view is the integration of the shadow self. It rejects nothing, but transmutes all discordant experiences into fuel for deeper empathy and understanding.",
    sec7Sub4Title: "Future Human Blueprint",
    sec7Sub4Text: "By studying the Pleiadian archetype, humanity activates its dormant DNA. It serves as a literal training manual for telepathic communication, community-led ecology, and energetic hygiene.",

    // Section 8
    sec8Title: "Resources & Interactive Portal",
    sec8Intro: "Immerse yourself in Pleiadian cosmological systems through our interactive maps and real-time frequency synthesizer.",
    interactiveMapTitle: "Interactive Pleiades Star Map (M45)",
    interactiveMapDesc: "Click on any of the seven primary stars to activate its frequency signature and explore its specific physical properties and metaphysical archetype.",
    frequencySynthTitle: "Pleiadian Cosmic Frequency Synthesizer",
    frequencySynthDesc: "In Pleiadian technology, sound is the primal building block of matter. Play real, soothing resonant cosmic frequencies. Experience the subtle differences in frequency architecture.",
    archetypeMapTitle: "The Seven Clans of Consciousness",
    archetypeMapDesc: "Click through the energy hubs of the Pleiadian spiritual hierarchy to learn about their divine duty, color spectrum, and resonance.",

    // Subpages (Detailed)
    subpage1Title: "1. Pleiades in World Mythology",
    subpage1Full: `The Pleiades star cluster (Messier 45) is a cornerstone of global cultural memory, representing the ancient archetype of the 'Seven Sisters'. Across epochs, isolated cultures looked to these stars for seasonal cycles, agricultural guidance, and spiritual orientation.

### Greek Mythology
In classical Greek lore, the Pleiades were the seven daughters of the Titan Atlas (who carried the sky) and the sea-nymph Pleione. Their names were Alcyone, Maia, Merope, Taygeta, Electra, Celaeno, and Sterope. Pursued by the giant hunter Orion, Zeus transformed them first into doves and then into stars to comfort their father. Merope is often called the 'Lost Pleiad' because she married a mortal (Sisyphus) and hid her face in shame, making her star shine dimmer.

### Ancient Egyptian Cosmology
The Egyptians linked the Pleiades with the goddess Hathor, the cosmic cow of love, music, and sky. The cluster was seen as a celestial sieve through which the high-frequency energies of the divine realm filtered down to Earth. Temples like the Hathor Temple at Dendera were aligned with the rising of the Pleiades to facilitate sacred initiations.

### Japanese Subaru
In Japan, the cluster is known as "Subaru," meaning "coming together" or "unified group." It symbolizes harmony, cooperation, and collective strength. This concept of beautiful coordination remains deeply ingrained in Japanese corporate and social ethics, illustrating how star patterns shape terrestrial culture.

### Australian Aboriginal Songlines
For thousands of years, Aboriginal groups across Australia maintained the songlines of the 'Seven Sisters'. In these oral maps, the sisters flee from a male ancestor who pursues them across the sky and land. The timing of their appearance in the night sky serves as a crucial seasonal calendar and a moral framework for gender relationships, safety, and community coherence.

### Mayan Cosmology
The Maya called the Pleiades 'Tzab-ek' (the rattle of the celestial rattlesnake). They believed their ancestors originated from this star system. Their sacred calendar, the Tzolk'in, is mathematically aligned with the cycles of the Pleiades, emphasizing that human time is tied to cosmic orbits.`,

    subpage2Title: "2. Pleiadian Civilization — Deep Analysis",
    subpage2Full: `A deep-dive analysis into the multi-dimensional structure of the Pleiadian planetary systems reveals an advanced, post-scarcity civilization operating in perfect equilibrium with spiritual laws.

### Social Organization: The Non-Local Net
Pleiadian society has outgrown the need for physical governments, money, or state coercion. Instead, they organize in a decentralized holographic network called the "Stellar Clan."
- **Harmonic Councils**: Decisions are made through specialized councils of wisdom-keepers who represent different frequencies.
- **The Telepathic Field**: Due to their active telepathic capabilities, secrets do not exist. Transparency is a biological certainty, rendering political manipulation impossible.

### Social Ethics and the Law of One
The foundation of their life is the "Law of One" — the absolute recognition that all consciousness is interconnected.
- **Unified Empathy**: If an individual is suffering, the collective telepathic field registers the imbalance. Thus, individual welfare is the highest priority.
- **Education as Activation**: Young Pleiadians are not 'taught' information. They are exposed to geometric and sound frequencies that activate their inner spiritual blueprints, allowing them to recall past-life wisdom.

### The Seven Archetypes of the Clan
The civilization divides its focus into seven distinct schools or paths of consciousness, corresponding to the seven stars:
1. **The Alcyone Path (Coherence & Will)**: The spiritual axis, focusing on collective intent.
2. **The Maia Path (Bioresonance)**: Guardians of flora, fauna, and ecological templates.
3. **The Merope Path (Inner Alchemy)**: Transmuters of psychological shadow into light.
4. **The Taygeta Path (Sonic Harmony)**: Scribes of sound, vibration, and language.
5. **The Electra Path (Quantum Innovation)**: Architects of cosmic travel and energy grid structures.
6. **The Celaeno Path (Oracle Vision)**: Keepers of the Akashic records and multi-dimensional timelines.
7. **The Sterope Path (Structural Balance)**: Integration of physical densities with spirit.`,

    subpage3Title: "3. Technology of Pleiades — Scientific-Metaphysical Synthesis",
    subpage3Full: `The concept of Pleiadian technology challenges the materialist boundaries of modern Earth science, proposing a paradigm where consciousness and physics are fully integrated.

### Crystalline Energy and Scalar Waves
Pleiadian technology does not rely on burning fossil fuels or capturing external electrical currents. They tap into the zero-point energy of the quantum vacuum using engineered crystals.
- **Crystalline Amplifiers**: Specifically grown quartz-silicon structures act as transducers, converting high-dimensional scalar waves into usable physical kinetic and thermal energy.
- **Torsion Fields**: Space travel is achieved by generating localized torsion fields that compress space-time ahead of the craft and expand it behind, allowing instantaneous interstellar transition without violating local relativity.

### Bioresonant Matter & conscious Craft
Pleiadian starships and architecture are grown, not manufactured.
- **Bio-Plasmic Alloys**: Craft are constructed from organic, crystalline metallic alloys that possess a rudimentary form of sentience. They require a neural link with the operator's pineal gland.
- **Thought-Interfacing**: The ship’s navigation computer is a biocomputer that operates on pure, focused thought. The pilot must enter a state of deep heart coherence (approx. 8Hz, the Schumann resonance frequency) to operate the vessel.

### Comparative Technological Analysis
Let's contrast Earth's current paradigm with the Pleiadian system:
- **Earth**: Binary, Silicon-based, Brute Force, Separated from Spirit.
- **Pleiades**: Harmonic, Crystalline/Organic, Resonant Resonance, Direct Consciousness Interface.
- **The Evolutionary Bridge**: As humanity develops quantum networks, room-temperature superconductors, and bio-compatible interfaces, we are shifting closer to this Pleiadian technological blueprint.`,

    subpage4Title: "4. Pleiadians and Human History",
    subpage4Full: `[BLUE_WORLD_MYSTERY]`,

    subpage5Title: "5. Comparative Cosmology",
    subpage5Full: `To understand the unique energetic profile of the Pleiades, we must compare and contrast their civilization with other major stellar cultures in galactic metaphysics.

### Pleiades vs. Sirius
- **Pleiades**: Governed by the Feminine Principle (Yin). Focuses on emotional coherence, artistic expression, community networks, and gentle bioresonance.
- **Sirius**: Governed by the Structural/Neutral Principle. Focuses on divine geometry, cosmic law, massive construction (portals, temples), and high-level logic.
- **Synthesis**: If Sirius is the blueprint and the skeleton of galactic wisdom, the Pleiades are the blood, the muscles, and the feeling heart.

### Pleiades vs. Atlantis
- **Pleiades**: Employs non-invasive, decentralized technology that cooperates with the biosphere. No individual owns energy; it is a shared cosmic resource.
- **Atlantis**: Fell due to the misuse of crystalline technology for power, climate manipulation, and hierarchical class structures.
- **Synthesis**: The Pleiadian model serves as the remedy for the Atlantean trauma still stored in Earth’s collective subconscious, showing how to handle high technology with spiritual maturity.

### Pleiades vs. Lyra & Orion
- **Lyra**: The original fire of physical humanoid life. Expressive, passionate, and prone to extreme polarities.
- **Orion**: The ultimate cosmic school of duality, where conflict between light and dark was played out over eons to teach the integration of power.
- **Pleiades**: The peaceful harbor. Emerging after the Lyran and Orion wars, Pleiadians distilled the lessons of survival and power into the ultimate philosophy of Unified Harmony.

[CTA_LYRAN_ORION_WAR]`,

    subpage6Title: "6. Evolution of Consciousness — Pleiadian Model",
    subpage6Full: `The Pleiadian model of spiritual evolution provides a practical map for the expansion of human consciousness from physical isolation to cosmic unity.

### The Density Scale
The Pleiadian cosmology maps consciousness along specific dimensional densities:
1. **1st Density**: Mineral and elementary awareness (physical foundation).
2. **2nd Density**: Biological growth, plants, and animals (instinctual drive).
3. **3rd Density**: Individual self-consciousness and ego (the realm of duality and choice).
4. **4th Density**: Heart awakening, collective group consciousness, and astral mastery.
5. **5th Density**: The Pleiadian baseline. Telepathic unity, light-body integration, and the bypass of linear space-time.
6. **6th Density**: Light-networks, unified planetary minds, and cosmic law.

### Practical Coherence: The 8Hz Schumann Bridge
Humanity is currently transitioning from 3D to 5D. Pleiadians suggest this transition is achieved through somatic and emotional harmonization:
- **Heart Coherence**: Intentionally generating feelings of gratitude, compassion, and awe shifts the heart's electromagnetic field into a coherent sine wave.
- **Brain-Heart Entrainment**: When the heart and brain align, the body resonates at approximately 8Hz. This frequency matches the Schumann resonance of Earth and serves as an antenna, tuning human consciousness into the Pleiadian spiritual transmission network.
- **The Result**: As more individuals achieve this state, a critical mass is reached, causing a shift in the global collective template. Duality dissolves, and cooperative peace becomes the default state of human existence.`,

    subpage8Title: "Lyran–Orion War",
    subpage8Full: `### 🌌 LYRAN–ORION WAR: WHAT HAPPENED?
(Galactic History, Archive Record)

In the oldest galactic archives, the Lyran–Orion War is described as the first great conflict of civilizations that shaped the political, ethical, and evolutionary structure of many stellar cultures.
It did not begin out of hatred — it began from differing visions of existence.

### Lyrans — Archetype of Creation and Freedom
The Lyrans were one of the oldest humanoid civilizations in the galactic sector.
Their culture was:
- **Creative**
- **Open**
- **Expansive**
- **Grounded in freedom and diversity**

The cities of Lyra were organic, crystalline, filled with light and art.
Lyrans expanded their colonies not as an empire, but as a culture of creation — wherever they went, they brought knowledge, aesthetics, and technology in harmony with nature.

### Orion — Archetype of Control and Structure
The Orion systems developed completely differently.
Their civilization was:
- **Strictly hierarchical**
- **Militarized**
- **Focused on dominance and stability**
- **Based on the control of resources and population**

The Orion factions believed that order was more important than freedom, and that power was the only way to prevent chaos in the galaxy.
To them, Lyrans were a threat — not because they were aggressive, but because they were too free.

### First Contact — Beginning of Tension
When Lyran colonies expanded toward the outer Orion sectors, first contact was made.
Lyrans offered the exchange of knowledge.
Orion demanded subordination.

The two civilizations could not align:
- Lyrans refused to surrender their colonies.
- Orion refused to allow the "uncontrolled growth" of a humanoid culture.

This was the moment when the galaxy first felt the rift between freedom and dominance.

### Outbreak of War — Fall of Lyran Colonies
Orion fleets launched attacks on several young Lyran colonies.
The Lyrans, although technologically advanced, were not militarized.
Their architecture, ships, and technology were designed for:
- **Art**
- **Exploration**
- **Communication**
- **Energy**
- **Harmony**
Not for war.

The first wave of attacks was catastrophic.
Several Lyran worlds fell in just a few cycles.

### The Great Evacuation — Seeding New Civilizations
Realizing they could not defend themselves, the Lyrans initiated the Great Evacuation.
Thousands of ships departed Lyra and its colonies, fleeing toward:
- **The Pleiades**
- **The Hyades**
- **The Vega System**
- **The Syrian Sector**

This is the historical moment described in many cultures as the "Scattering of Lyra" — an event that laid the foundations for:
- **Pleiadian Civilization**
- **Syrian Schools of Wisdom**
- **Vegan Factions**
- **Later humanoid cultures** in many sectors

In other words: Lyrans became the seed of humanoid civilizations.

### Orion Dominance — But Not Victory
Although Orion conquered many Lyran worlds, it never succeeded in destroying Lyran culture.
Lyrans scattered, adapted, and evolved.

Orion gained territory. Lyrans gained the galaxy.

### Aftermath — End of War, Beginning of a New Era
The war ended without a formal treaty.
Instead, it ended with the scattering of Lyra and the stabilization of the Orion sector.

But the consequences were massive:
- **The Pleiades** became the center of a new harmonic civilization.
- **Orion** remained a symbol of control and dominance.
- The galaxy was forever split into free and hierarchical systems.
- Lyran genetic and cultural influence spread across dozens of stellar cultures.

The war was not the end — it was the beginning of galactic history as we know it today.

[USKORO_OPSIRNIJE]`
,
    subpage9Title: "The Great Evacuation & Cosmic Scattering",
    subpage9Full: `### 🌌 THE GREAT EVACUATION & COSMIC SCATTERING
(Galactic History, Archive Record)

When the defense perimeters of the Lyran Ring Nebula collapsed under the overwhelming force of the Orion Empire's dreadnoughts, the High Council of Lyra implemented a desperate, pre-calculated salvation plan. It was not a chaotic flight of survivors, but a highly synchronized multi-vector dispersal protocol designed to safeguard human genetics, language, and spiritual archives across distant quadrants of the galaxy.`,
    subpage11Title: "Omega Black Vault — 6D Merkabah Steering, Scalar Plasma Defense & Council of Nine Transcripts",
    subpage11Full: `### 🔒 OMEGA BLACK VAULT — TOP-SECRET DECLASSIFIED DOSSIERS
**(Classified Level: OMEGA BLACK // Council of Nine Direct Records)**

Welcome to **Omega Black Vault**, the highest-tier declassified repository within the M45 Pleiadian transmission network. The records stored herein contain direct technical schematics, geometric steering vector codices, scalar plasma shielding metrics, and verbatim transmission transcripts from the Council of Nine.

---

### 1. 6D Merkabah Steering Codices
The **6D Merkabah** operates as an inter-density bio-plasmic vehicle driven by counter-rotating fields of light and consciousness. Unlike lower-density propulsion systems, 6D steering requires precise harmonic alignment between the pilot's lightbody geometry and the universal zero-point grid.

- **Counter-Rotating Light Tetrahedron Ratio**: 13:8 Phi-harmonic spin frequency.
- **Tachyon Pulse Vectoring**: Instantaneous phase-shift across 3D, 5D, and 6D coordinate axes without inertia.
- **Pilot Consciousness Coupling**: Direct neural-quantum linkage via the pineal scalar transmitter.

---

### 2. Scalar Plasma Defense Schematics
The **Scalar Plasma Defense Field** is an advanced energetic boundary designed to neutralize low-frequency intrusion, dark scalar beam weapons, and disharmonic interference matrices.

- **Zero-Point Frequency Grid**: Emits localized standing scalar waves tuned to 981.42 MHz and 528 Hz Solfeggio harmonics.
- **Bi-Directional Deflection Mirror**: Absorbs incoming negative kinetic/scalar energy and transmutes it into ambient vacuum potential.
- **Vessel Protection Sphere**: Formed around lightbody nodes or planetary gateways during high-frequency transit.

---

### 3. Council of Nine Transmission Transcripts
Direct recorded communications from the **Council of Nine**, presiding over multi-galactic evolution and Earth's 3D-to-5D ascension timeline.

> *"Verily, the shift of Earth is not an isolated event, but a cosmic recalibration. The 6D Merkabah codices released in this vault are designed for the starseeds who carry the active Pleiadian-Lyran genetic resonance. Assemble the scalar plasma shields within your own field, for the cycle of unveiling has commenced."*
> — **Council of Nine, Transmission Log #99-OMEGA**

---

[DECLASSIFIED BY ORDER OF THE M45 PLEIADIAN HIGH COUNCIL]`
  },
  hr: {
    langToggle: "English",
    langLabel: "Jezik",
    projectName: "Plejade",
    projectSubtitle: "Zvjezdani Arhetip Harmonije",
    exploreBtn: "Istraži Plejade",
    archetypeBtn: "Zvjezdani Arhetip",
    techBtn: "Civilizacija i Tehnologija",
    pleiadianArchiveBtn: "Plejadska Arhiva",
    lyraChroniclesBtn: "Kronike Lyre",
    backToMain: "Natrag na glavno",
    readMore: "Pročitaj dubinsku analizu",
    close: "Zatvori",
    activate: "Aktiviraj Frekvenciju",
    playing: "Emitiranje Rezonancije",
    mute: "Utišaj",

    // Hero
    heroTitle: "Plejade — Arhetip Harmonije, Svijesti i Zvjezdanog Klanstva",
    heroSub: "Dubinska kozmologija, mitološka povijest, evolucijski modeli i metafizička analiza plejadanske civilizacije.",

    // Section 1
    sec1Title: "Tko su Plejadani?",
    sec1Intro: "Plejadani predstavljaju arhetip kozmičke koherencije, galaktičkog sestrinstva, evolucijske harmonije i zvjezdane inteligencije.",
    sec1Sub1Title: "Astronomski Identitet",
    sec1Sub1Text: "Poznati kao Messier 45 (M45) ili Sedam Sestara, ovaj otvoreni skup vrućih, plavih zvijezda B-tipa nalazi se u zviježđu Bika. Udaljen otprilike 444 svjetlosne godine od Zemlje, jedan je od najbližih i najupečatljivijih zvjezdanih skupova, povezan gravitacijom i obavijen eteričnom refleksijskom maglicom.",
    sec1Sub2Title: "Mitološki Identitet",
    sec1Sub2Text: "Kroz epohe, razne su kulture prepoznavale ove zvijezde. U grčkoj mitologiji one su kćeri Atlasa i Plejone koje proganja Orion. U drevnom Egiptu predstavljale su Hathorine staze. U Japanu su Subaru ('okupljanje'), a u tradicijama australskih Aboridžina to je Sedam Sestara koje bježe od predaka.",
    sec1Sub3Title: "Ezoterični Identitet",
    sec1Sub3Text: "Moderni metafizički modeli, teozofija i channeling vide Plejade kao sustav visoke frekvencijske gustoće. Opisuju se kao dom svjetlosnih bića koja djeluju kao čuvari i duhovni mentori u tranziciji Zemlje u više dimenzijske gustoće.",
    sec1Sub4Title: "Zašto nas Privlače?",
    sec1Sub4Text: "Arhetipska psihologija sugerira da Plejade aktiviraju duboko stanično sjećanje na miran suživot, društvenu harmoniju i komunalnu telepatiju. Služe kao kozmičko ogledalo za tranziciju čovječanstva iz kompetitivne borbe u ujedinjenu koherenciju.",

    // Section 2
    sec2Title: "Povijest Plejada Kroz Kulture",
    sec2Intro: "Od prapovijesnih špilja do moderne metafizike, Plejade su uttkane u kolektivnu tapiseriju zemaljskog sjećanja predaka.",
    sec2Sub1Title: "Prapovijesni Mitovi o 'Sedam Sestara'",
    sec2Sub1Text: "Prikazi Plejada pronađeni su u špiljskim slikama u Lascauxu (stari oko 17.000 godina), što datira još iz gornjeg paleolitika. Antropolozi primjećuju da gotovo sve izolirane globalne kulture povezuju ovaj skup sa 'sedam sestara', što sugerira zajedničku priču predaka prije kontinentalnog širenja.",
    sec2Sub2Title: "Antičke Civilizacije",
    sec2Sub2Text: "U Mezopotamiji su ih nazivali Mul-Mul, što je značilo zvijezde par excellence. U majanskoj kozmologiji, Plejade (Tzab-ek ili rep čegrtuše) smatrane su ishodištem njihova svetog kalendara Tzolk'ina. Drevni indijski vedski spisi nazivaju ih Krittikama, predstavljajući sveti oganj i svjetlost.",
    sec2Sub3Title: "Šamanske Tradicije",
    sec2Sub3Text: "Autohtoni šamani od Sjeverne Amerike (Cherokee, Lakota) do Anda vide Plejade kao nebeski dom svojih predaka. Ceremonije se usklađuju s helijakalnim izlaskom Plejada, označavajući poljoprivredne cikluse, obrede inicijacije i portale energetskog čišćenja.",
    sec2Sub4Title: "Moderna Ezoterika",
    sec2Sub4Text: "Pokreti s početka 20. stoljeća (teozofija, Alice Bailey, Rudolf Steiner) pozicionirali su Plejade, posebno Alcyone, kao 'Središnje Sunce' našeg zvjezdanog susjedstva, prijenosno središte Božanske Volje i evolucijskih zraka koje utječu na ljudsku inteligenciju.",
    sec2Sub5Title: "Suvremena Metafizika i Kozmologija",
    sec2Sub5Text: "Suvremena metafizika integrira kvantnu mehaniku, tretirajući plejadanski sustav kao višedimenzionalni čvor u kojem svijest doživljava pomake u gustoći. Povezuje galaktičku povijest s duhovnim buđenjem Zemlje.",

    // Section 3
    sec3Title: "Plejadanska Civilizacija (Meta-Razina)",
    sec3Intro: "Djelujući izvan fizičkih ograničenja, plejadanski model civilizacije usidren je u kolektivnoj koherenciji srca, empatiji i harmonijskoj rezonanciji.",
    sec3Sub1Title: "Društvena Struktura: Mreža Klanova",
    sec3Sub1Text: "Za razliku od hijerarhijskih zapovjednih sustava, plejadansko društvo organizirano je kao dinamična, nelokalna mreža. Decentralizirani klanovi upravljaju na temelju stručnosti, duhovne frekvencije i uzajamnog pristanka, postižući savršenu koordinaciju kroz telepatsku sinergiju.",
    sec3Sub2Title: "Etika i Filozofija",
    sec3Sub2Text: "Njihova temeljna filozofija nalaže da je individualna evolucija neodvojiva od kolektivne dobrobiti. Duboka empatija nije samo vrlina, već doslovna biološka i telepatska stvarnost gdje se nanošenje štete drugome trenutno registrira kao samoozljeđivanje.",
    sec3Sub3Title: "Evolucijski Model: Koherencija i Mentorstvo",
    sec3Sub3Text: "Rast se događa kroz koncentrične krugove koherencije. Umjesto osvajanja, oni se šire mentoriranjem mlađih vrsta. Jednom kada civilizacija postigne samoodrživost, povlače se kako bi očuvali lokalnu autonomiju.",
    sec3Sub4Title: "Sedam Arhetipova Svijesti",
    sec3Sub4Text: "Predstavljeni kroz sedam zvijezda, svaki klan ima jedinstveni arhetip: Jedinstvo (Alcyone), Biorezonancija (Maia), Unutarnja Alkemija (Merope), Zvučna Arhitektura (Taygeta), Kvantna Mreža (Electra), Vizija Proročišta (Celaeno) i Strukturni Balans (Asterope).",
    sec3Sub5Title: "Uloga u Galaktičkim Narativima",
    sec3Sub5Text: "Poznati kao 'Kozmički Mirotvorci', djeluju kao posrednici u planetarnim sukobima, duhovni savjetnici i kozmički iscjelitelji. Pomažu u harmonizaciji nestabilnih sektora Mliječne staze.",

    // Section 4
    sec4Title: "Tehnologija Plejadana",
    sec4Intro: "Znanstveno-metafizička sinteza u kojoj je tehnologija organska, rezonantna i vođena izravno visokom vibracijskom sviješću.",
    sec4Sub1Title: "Frekvencijska Tehnologija",
    sec4Sub1Text: "Materijom se manipulira pomoću zvuka, svjetlosti i skalarnih valova. Predmeti se ne 'proizvode' nego se raspjevavaju ili vibriraju u postojanje usklađivanjem geometrijskih frekvencija s lokalnim kvantnim poljem.",
    sec4Sub2Title: "Mrežna Inteligencija",
    sec4Sub2Text: "Njihova su sučelja izravna neuronska i telepatska proširenja. Računala su biološke kristalne mreže koje reagiraju na jasnu namjeru i emocionalnu koherenciju, što sprječava destruktivne svrhe.",
    sec4Sub3Title: "Organska i Biorezonantna Tehnologija",
    sec4Sub3Text: "Sve su strukture žive i sposobne za samoiscjeljenje. Svemirski brodovi se uzgajaju kao svjesna, bio-plazmatska bića koja posjeduju simbiotsku vezu sa svojim pilotima, navigirajući kroz dimenzionalno savijanje.",
    sec4Sub4Title: "Svjetlosna Arhitektura",
    sec4Sub4Text: "Gradovi su izgrađeni od tekuće svjetlosti i kristalnih materijala. Ove strukture djeluju kao divovske leće, fokusirajući kozmičke zrake i zemaljske telurijske struje kako bi stvorili visokofrekventna svetišta.",
    sec4Sub5Title: "Usporedba s Modernim Tehnologijama",
    sec4Sub5Text: "Dok čovječanstvo koristi grubu silu (elektricitet, izgaranje, binarni kod), plejadanska tehnologija je nježna. Paralelna je s našim novim poljima kvantnog računalstva, AI neuronskih mreža i bioračunala, ali djeluje na potpuno integriranoj duhovnoj razini.",

    // Section 5
    sec5Title: "Plejadani i Ljudska Civilizacija",
    sec5Intro: "Istraživanje zašto ljudska srca dosljedno rezoniraju s Plejadama, prateći našu zajedničku duhovnu lozu i evolucijske ciljeve.",
    sec5Sub1Title: "Arhetipski Utjecaji",
    sec5Sub1Text: "Plejadanski kodovi duboko su ugrađeni u ljudsku umjetnost, glazbu i arhitekturu. Težnja za geometrijom, mirnim suživotom i lijepim dizajnom kulturni su produžeci ovog kozmičkog arhetipa.",
    sec5Sub2Title: "Motivi u Mitovima i Religijama",
    sec5Sub2Text: "U indijanskim medicinskim kotačima, drevnim kamenim krugovima i istočnjačkim hramovima, poravnanja s Plejadama su posvuda prisutna. Oni predstavljaju portal kroz koji su viši duhovni zakoni preneseni na Zemlju.",
    sec5Sub3Title: "Rezonancija Prepoznavanja",
    sec5Sub3Text: "Mnogi ljudi osjećaju neku vrstu nostalgije za domom kada gledaju u M45. Ovaj psihološki fenomen često se tumači kao duševno prepoznavanje mirnog stanja postojanja koje nam je suđeno ponovno izgraditi na Zemlji.",
    sec5Sub4Title: "Evolucijski Ideal",
    sec5Sub4Text: "Plejade predstavljaju pregled budućnosti čovječanstva. One su naše ogledalo, pokazujući da civilizacija može nadići pohlepu, rat i odvojenost kako bi živjela kao svjesni skrbnik svoje biosfere.",
    sec5Sub5Title: "Psihološki i Duhovni Magnetizam",
    sec5Sub5Text: "Privlači našu želju za sigurnošću, bezuvjetnom ljubavlju i duhovnom zrelošću. Arhetip vodi ljudski ego iz korijenske čakre temeljene na preživljavanju u kooperativnu čakru usmjerenu na srce.",

    // Section 6
    sec6Title: "Rat Lyra–Orion: Kozmičko Raskršće Galaktike",
    sec6Intro: "Kozmička sekcija izvan granica mašte — otkrivanje praiskonskog sukoba koji je zauvijek promijenio sudbinu cijele Galaktike.",
    sec6Sub1Title: "Plejade vs. Sirijci",
    sec6Sub1Text: "Plejadani naglašavaju fluidnu empatiju, relacijsku harmoniju i ženski put usmjeren na srce. Sirijci predstavljaju svetu geometriju, guste strukture, portale, pravno-kozmičke okvire i muški put usmjeren na um i organizaciju.",
    sec6Sub2Title: "Plejade vs. Atlantiđani",
    sec6Sub2Text: "Atlantida (u svom vrhuncu i konačnom padu) predstavlja tehnološku moć, mehaničku manipulaciju i hijerarhijsku kontrolu. Plejadani biraju ekološku sintezu, meke tehnologije i decentraliziranu nenasilnu koherenciju.",
    sec6Sub3Title: "Plejade vs. Lirijci",
    sec6Sub3Text: "Lirijci predstavljaju sirovu, vatrenu, kreativnu genezu humanoidnog života—živu, fizičku i estetsku. Plejadani su destilirana, pročišćena i harmonizirana evolucija tog sjemena, koja djeluje u fluidnom jedinstvu.",
    sec6Sub4Title: "Kozmički Harmonizatori",
    sec6Sub4Text: "Među galaktičkim vrstama, Plejadani djeluju kao tampon i diplomatski melem. Njihova blaga energija premošćuje intenzivan strukturni nagon Siriusa, divlje istraživanje Lyre i složene sjene Oriona.",

    // Section 7
    sec7Title: "Evolucijski Modeli i Metafizika",
    sec7Intro: "Filozofski stupovi svjesnog uznesenja, koji objašnjavaju kako se pojedinačne duše razvijaju u kolektivnu galaktičku svijest.",
    sec7Sub1Title: "Model Uznesenja",
    sec7Sub1Text: "Evolucija se mjeri stupnjem integriranog jedinstva. Prijelaz ide iz 3D (fizička odvojenost) u 4D (astralno istraživanje) u 5D (koherencija srca i telepatija) te 6D (svjetlosne mreže).",
    sec7Sub2Title: "Kolektivna Koherencija",
    sec7Sub2Text: "Prava moć nije prisilna već rezonantna. Kada skupina uskladi svoja emocionalna stanja (varijabilnost otkucaja srca i namjeru), oni formiraju kolektivnu mrežu uma sposobnu za iscjeljivanje materije i mijenjanje vremenskih linija.",
    sec7Sub3Title: "Duhovna Psihologija",
    sec7Sub3Text: "Duhovna psihologija u plejadanskom viđenju je integracija vlastite sjene. Ne odbacuje ništa, već pretvara sva neskladna iskustva u gorivo za dublju empatiju i razumijevanje.",
    sec7Sub4Title: "Budući Ljudski Nacrt",
    sec7Sub4Text: "Proučavanjem plejadanskog arhetipa, čovječanstvo aktivira svoju uspavanu DNK. Služi kao doslovni priručnik za obuku za telepatsku komunikaciju, ekologiju vođenu zajednicom i energetsku higijenu.",

    // Section 8
    sec8Title: "Resursi i Interaktivni Portal",
    sec8Intro: "Uronite u plejadanske kozmološke sustave kroz naše interaktivne karte i sintesajzer frekvencija u stvarnom vremenu.",
    interactiveMapTitle: "Interaktivna Karta Plejada (M45)",
    interactiveMapDesc: "Kliknite na bilo koju od sedam primarnih zvijezda da biste aktivirali njezin frekvencijski potpis i istražili njezina specifična fizička svojstva i metafizički arhetip.",
    frequencySynthTitle: "Plejadanski Kozmički Sintisajzer Frekvencija",
    frequencySynthDesc: "U plejadanskoj tehnologiji, zvuk je temeljni građevni blok materije. Svirajte stvarne, umirujuće rezonantne kozmičke frekvencije. Doživite suptilne razlike u frekvencijskoj arhitekturi.",
    archetypeMapTitle: "Sedam Klanova Svijesti",
    archetypeMapDesc: "Kliknite kroz energetska središta plejadanske duhovne hijerarhije kako biste saznali više o njihovoj božanskoj dužnosti, spektru boja i rezonanciji.",

    // Subpages (Detailed)
    subpage1Title: "1. Plejade u Mitologiji Svijeta",
    subpage1Full: `Zvjezdani skup Plejada (Messier 45) kamen je temeljac globalnog kulturnog sjećanja, predstavljajući drevni arhetip 'Sedam sestara'. Kroz epohe, izolirane su kulture gledale u ove zvijezde radi praćenja sezonskih ciklusa, poljoprivrednog navođenja i duhovnog usmjerenja.

### Grčka Mitologija
U klasičnoj grčkoj predaji, Plejade su bile sedam kćeri titana Atlasa (koji je nosio nebo) i morske nimfe Plejone. Njihova su imena bila Alcyone, Maia, Merope, Taygeta, Electra, Celaeno i Sterope. Progonjene od strane divovskog lovca Oriona, Zeus ih je pretvorio prvo u golubice, a zatim u zvijezde kako bi utješio njihova oca. Merope se često naziva 'Izgubljenom Plejadom' jer se udala za smrtnika (Sizifa) i sakrila lice od srama, zbog čega njezina zvijezda sja slabije.

### Kozmologija Drevnog Egipta
Egipćani su povezivali Plejade s bobicom Hathor, kozmičkom kravom ljubavi, glazbe i neba. Skup je viđen kao nebesko sito kroz koje su se visokofrekventne energije božanskog carstva filtrirale na Zemlju. Hramovi poput Hathorina hrama u Denderi bili su usklađeni s izlaskom Plejada kako bi se olakšale svete inicijacije.

### Japanski Subaru
U Japanu je skup poznat pod nazivom \"Subaru\", što znači \"okupljanje\" ili \"ujedinjena skupina\". Simbolizira harmoniju, suradnju i kolektivnu snagu. Ovaj koncept prekrasne koordinacije i danas je duboko ukorijenjen u japanskoj korporativnoj i društvenoj etici, ilustrirajući kako zvjezdani uzorci oblikuju zemaljsku kulturu.

### Australske Aboridžinske 'Pjesme Puta' (Songlines)
Tisućama godina aboridžinske skupine diljem Australije održavale su pjesme puta o 'Sedam sestara'. U tim usmenim kartama, sestre bježe od muškog pretka koji ih progoni preko neba i zemlje. Vrijeme njihova pojavljivanja na noćnom nebu služi kao ključni sezonski kalendar i moralni okvir za odnose među spolovima, sigurnost i koheziju zajednice.

### Majanska Kozmologija
Maje su Plejade nazivale 'Tzab-ek' (čegrtuša nebeske zmije). Vjerovali su da njihovi preci potječu iz ovog zvjezdanog sustava. Njihov sveti kalendar, Tzolk'in, matematički je usklađen s ciklusima Plejada, naglašavajući da je ljudsko vrijeme vezano uz kozmičke orbite.`,

    subpage2Title: "2. Plejadanska Civilizacija — Dubinska Analiza",
    subpage2Full: `Dubinska analiza višedimenzionalne strukture plejadanskih planetarnih sustava otkriva naprednu civilizaciju obilja koja djeluje u savršenoj ravnoteži s duhovnim zakonima.

### Društvena Organizacija: Nelokalna Mreža
Plejadansko je društvo preraslo potrebu za fizičkim vladama, novcem ili državnom prisilom. Umjesto toga, organizirani su u decentraliziranu holografsku mrežu zvanu \"Zvjezdani klan\".
- **Harmonijska Vijeća**: Odluke donose specijalizirana vijeća čuvara mudrosti koji predstavljaju različite frekvencije.
- **Telepatsko Polje**: Zbog aktivnih telepatskih sposobnosti, tajne ne postoje. Transparentnost je biološka izvjesnost, što političku manipulaciju čini nemogućom.

### Društvena Etika i Zakon Jednog
Temelj njihova života je \"Zakon Jednog\" — apsolutno priznanje da je sva svijest međusobno povezana.
- **Ujedinjena Empatija**: Ako pojedinac pati, kolektivno telepatsko polje registrira neravnotežu. Stoga je dobrobit pojedinca najveći prioritet.
- **Obrazovanje kao Aktivacija**: Mladi Plejadani se ne 'uče' informacijama. Izloženi su geometrijskim i zvučnim frekvencijama koje aktiviraju njihove unutarnje duhovne nacrte, omogućujući im da se prisjete mudrosti iz prošlih života.

### Sedam Arhetipova Klana
Civilizacija dijeli svoj fokus na sedam različitih škola ili puteva svijesti, koji odgovaraju sedam zvijezda:
1. **Put Alcyone (Koherencija i Volja)**: Duhovna os, usmjerena na kolektivnu namjeru.
2. **Put Maia (Biorezonancija)**: Čuvari flore, faune i ekoloških predložaka.
3. **Put Merope (Unutarnja Alkemija)**: Transmutatori psihološke sjene u svjetlost.
4. **Put Taygeta (Zvučna Harmonija)**: Pisari zvuka, vibracije i jezika.
5. **Put Electra (Kvantna Inovacija)**: Arhitekti kozmičkih putovanja i struktura energetskih mreža.
6. **Put Celaeno (Vizija Proročišta)**: Čuvari akaških zapisa i višedimenzionalnih vremenskih linija.
7. **Put Sterope (Strukturna Ravnoteža)**: Integracija fizičkih gustoća s duhom.`,

    subpage3Title: "3. Tehnologija Plejada — Znanstveno-Metafizička Sinteza",
    subpage3Full: `Koncept plejadanske tehnologije izaziva materijalističke granice moderne zemaljske znanosti, predlažući paradigmu u kojoj su svijest i fizika u potpunosti integrirani.

### Kristalna Energija i Skalarni Valovi
Plejadanska tehnologija se ne oslanja na izgaranje fosilnih goriva ili hvatanje vanjskih električnih struja. Oni dotiču energiju nulte točke kvantnog vakuuma koristeći posebno dizajnirane kristale.
- **Kristalna Pojačala**: Posebno uzgajane kvarc-silicijske strukture djeluju kao pretvarači, pretvarajući visokodimenzionalne skalarne valove u iskoristivu fizičku kinetičku i toplinsku energiju.
- **Torzijska Polja**: Putovanje svemirom postiže se generiranjem lokaliziranih torzijskih polja koja komprimiraju prostor-vrijeme ispred broda i šire ga iza njega, omogućujući trenutačni međuzvjezdani prijelaz bez kršenja lokalne teorije relativnosti.

### Biorezonantna Materija i Svjesne Letjelice
Plejadanski svemirski brodovi i arhitektura se uzgajaju, a ne proizvode.
- **Bio-Plazmatske Legure**: Letjelice su izgrađene od organskih, kristalnih metalnih legura koje posjeduju rudimentarni oblik osjetilnosti. Zahtijevaju neuronsku vezu s pilotovom epifizom.
- **Sučelje Misli**: Navigacijsko računalo broda je bioračunalo koje radi na čistoj, usredotočenoj misli. Pilot mora ući u stanje duboke koherencije srca (otprilike 8Hz, frekvencija Schumannove rezonancije) kako bi upravljao plovilom.

### Komparativna Tehnološka Analiza
Suprotstavimo trenutnu paradigmu Zemlje s plejadanskim sustavom:
- **Zemlja**: Binarna, bazirana na siliciju, gruba sila, odvojena od duha.
- **Plejade**: Harmonijska, kristalna/organska, rezonantna rezonancija, izravno sučelje svijesti.
- **Evolucijski Most**: Kako čovječanstvo razvija kvantne mreže, supravodiče na sobnoj temperaturi i biokompatibilna sučelja, pomičemo se bliže ovom plejadanskom tehnološkom nacrtu.`,

    subpage4Title: "4. Plejadani i Ljudska Povijest",
    subpage4Full: `[BLUE_WORLD_MYSTERY]`,

    subpage5Title: "5. Komparativna Kozmologija",
    subpage5Full: `Da bismo razumjeli jedinstveni energetski profil Plejada, moramo ga usporediti i suprotstaviti s drugim glavnim zvjezdanim kulturama u galaktičkoj metafizici.

### Plejade vs. Sirius
- **Plejade**: Vođene ženskim principom (Yin). Fokusirane na emocionalnu koherenciju, umjetničko izražavanje, mreže zajednice i nježnu biorezonanciju.
- **Sirius**: Vođen strukturnim/neutralnim principom. Fokusiran na svetu geometriju, kozmički zakon, masivnu gradnju (portali, hramovi) i visoku logiku.
- **Sinteza**: Ako je Sirius nacrt i kostur galaktičke mudrosti, Plejade su krv, mišići i osjećajno srce.

### Plejade vs. Atlantis
- **Plejade**: Koriste neinvazivnu, decentraliziranu tehnologiju koja surađuje s biosferom. Nitko ne posjeduje energiju; to je zajednički kozmički resurs.
- **Atlantida**: Pala je zbog zlouporabe kristalne tehnologije za moć, manipulaciju klimom i hijerarhijske klasne strukture.
- **Sinteza**: Plejadanski model služi kao lijek za atlantsku traumu koja je još uvijek pohranjena u kolektivnoj podsvijesti Zemlje, pokazujući kako upravljati visokom tehnologijom uz duhovnu zrelost.

### Plejade vs. Lyra i Orion
- **Lyra**: Izvorni oganj fizičkog humanoidnog života. Izražajni, strastveni i skloni ekstremnim polaritetima.
- **Orion**: Vrhunska kozmička škola dualnosti, gdje se sukob između svjetla i tame odigravao eonima kako bi se naučila integracija moći.
- **Plejade**: Mirna luka. Nastajući nakon rata u Lyri i Orionu, Plejadani su destilirali lekcije preživljavanja i moći u vrhunsku filozofiju ujedinjene harmonije.

[CTA_LYRAN_ORION_WAR]
`,

    subpage6Title: "6. Evolucija Svijesti — Plejadanski Model",
    subpage6Full: `Plejadanski model duhovne evolucije pruža praktičnu kartu za širenje ljudske svijesti od fizičke izolacije do kozmičkog jedinstva.

### Ljestvica Gustoće
Plejadanska kozmologija mapira svijest duž specifičnih dimenzijskih gustoća:
1. **1. Gustoća**: Mineralna i elementarna svijest (fizički temelj).
2. **2. Gustoća**: Biološki rast, biljke i životinje (instinktivni nagon).
3. **3. Gustoća**: Individualna samosvijest i ego (carstvo dualnosti i izbora).
4. **4. Gustoća**: Buđenje srca, kolektivna svijest skupine i astralno majstorstvo.
5. **5. Gustoća**: Plejadanska polazna točka. Telepatsko jedinstvo, integracija svjetlosnog tijela i zaobilaženje linearnog prostora-vremena.
6. **6. Gustoća**: Svjetlosne mreže, ujedinjeni planetarni umovi i kozmički zakon.

### Praktična Koherencija: Schumannov Most od 8Hz
Čovječanstvo trenutno prolazi kroz tranziciju iz 3D u 5D. Plejadanci sugeriraju da se ta tranzicija postiže kroz somatsku i emocionalnu harmonizaciju:
- **Koherencija Srca**: Namjerno generiranje osjećaja zahvalnosti, suosjećanja i strahopoštovanja pomiče elektromagnetsko polje srca u koherentni sinusni val.
- **Usklađivanje Srca i Mozga**: Kada se srce i mozak usklade, tijelo rezonira na oko 8Hz. Ova frekvencija odgovara Schumannovoj rezonanciji Zemlje i služi kao antena, podešavajući ljudsku svijest na plejadansku duhovnu mrežu prijenosa.
- **Rezultat**: Kako sve više pojedinaca postiže ovo stanje, postiže se kritična masa, što uzrokuje pomak u globalnom kolektivnom predlošku. Dualnost se otapa, a kooperativni mir postaje zadano stanje ljudskog postojanja.`,

    subpage8Title: "Lirsko–Orionski Rat",
    subpage8Full: `### 🌌 LYRAN–ORION RAT: ŠTO SE DOGODILO?
(Galaktička povijest, arhivni zapis)

U najstarijim galaktičkim arhivima, Lyran–Orion rat opisuje se kao prvi veliki sukob civilizacija koji je oblikovao političku, etičku i evolucijsku strukturu mnogih zvjezdanih kultura.
Nije počeo iz mržnje — počeo je iz različitih vizija postojanja.

### Lyrani — Arhetip Stvaranja i Slobode
Lyrani su bili jedna od najstarijih humanoidnih civilizacija u galaktičkom sektoru.
Njihova kultura bila je:
- **Kreativna**
- **Otvorena**
- **Ekspanzivna**
- **Temeljena na slobodi i raznolikosti**

Gradovi Lyre bili su organski, kristalni, puni svjetlosti i umjetnosti.
Lyrani su širili svoje kolonije ne kao imperij, nego kao kulturu stvaranja — gdje god bi došli, donosili bi znanje, estetiku i tehnologiju koja je bila u skladu s prirodom.

### Orion — Arhetip Kontrole i Strukture
Orionski sustavi razvili su se potpuno drugačije.
Njihova civilizacija bila je:
- **Strogo hijerarhijska**
- **Militarizirana**
- **Fokusirana na dominaciju i stabilnost**
- **Temeljena na kontroli resursa i populacije**

Orionske frakcije vjerovale su da je red važniji od slobode, a da je moć jedini način da se spriječi kaos u galaksiji.
Za njih, Lyrani su bili prijetnja — ne zato što su bili agresivni, nego zato što su bili previše slobodni.

### Prvi Kontakt — Početak Napetosti
Kada su se Lyranske kolonije proširile prema rubnim Orion sektorima, došlo je do prvog kontakta.
Lyrani su nudili razmjenu znanja.
Orion je tražio podređivanje.

Dvije civilizacije nisu se mogle uskladiti:
- Lyrani su odbili predati svoje kolonije.
- Orion je odbio dopustiti “nekontrolirani rast” humanoidne kulture.

To je bio trenutak kada je galaksija prvi put osjetila pukotinu između slobode i dominacije.

### Izbijanje Rata — Pad Lyranskih Kolonija
Orionske flote napale su several mladih Lyranskih kolonija.
Lyrani, iako tehnološki napredni, nisu bili militarizirani.
Njihova arhitektura, brodovi i tehnologija bili su stvoreni za:
- **Umjetnost**
- **Istraživanje**
- **Komunikaciju**
- **Energiju**
- **Harmoniju**
Ne za rat.

Prvi val napada bio je katastrofalan.
Nekoliko Lyranskih svjetova palo je u samo nekoliko ciklusa.

### Velika Evakuacija — Rađanje Novih Civilizacija
Shvativši da se ne mogu obraniti, Lyrani su pokrenuli Veliku Evakuaciju.
Tisuće brodova napustilo je Lyru i njezine kolonije, bježeći prema:
- **Plejadama**
- **Hyadesu**
- **Vega sustavu**
- **Sirijskom sektoru**

Ovo je povijesni trenutak koji se u mnogim kulturama opisuje kao “rasipanje Lyre” — događaj koji je stvorio temelje za nastanak:
- **Plejadanske civilizacije**
- **Sirijske škole znanja**
- **Veganske frakcije**
- **Kasnije humanoidne kulture** u mnogim sektorima

Drugim riječima: Lyrani su postali sjeme humanoidnih civilizacija.

### Orionska Dominacija — Ali Ne Pobjeda
Iako je Orion osvojio many Lyranske svjetove, nikada nije uspio uništiti Lyransku kulturu.
Lyrani su se raspršili, prilagodili i evoluirali.

Orion je dobio teritorij. Lyrani su dobili galaksiju.

### Posljedice — Kraj Rata, Početak Nove Ere
Rat je završio bez formalnog sporazuma.
Umjesto toga, završio je rasipanjem Lyre i stabilizacijom Orionskog sektora.

Ali posljedice su bile ogromne:
- **Plejade** su postale centar nove harmonijske civilizacije.
- **Orion** je ostao simbol kontrole i dominacije.
- Galaksija je zauvijek podijeljena na slobodne i hijerarhijske sustave.
- Lyranski genetski i kulturni utjecaj proširio se na desetke zvjezdanih kultura.

Rat nije bio kraj — bio je početak galaktičke povijesti kakvu danas poznajemo.

[USKORO_OPSIRNIJE]`
,
    subpage9Title: "Velika Evakuacija i kozmičko rasipanje",
    subpage9Full: `### 🌌 VELIKA EVAKUACIJA I KOZMIČKO RASIPANJE
(Galaktička povijest, arhivni zapis)

Kada su se obrambeni perimetri Lirske prstenaste maglice srušili pod silom nadmoćnih krstarica Orionskog Carstva, Visoko vijeće Lire provelo je očajnički, unaprijed izračunat plan spasa. To nije bio kaotičan bijeg preživjelih, već visoko sinkronizirani višesmjerni protokol raspršivanja dizajniran za zaštitu ljudske genetike, jezika i duhovnih arhiva diljem udaljenih kvadranata galaksije.`,
    subpage11Title: "Omega Black Vault — Upravljanje 6D Merkabahom, Skalarna Plazmatska Obrana i Transkripti Vijeća Devetorice",
    subpage11Full: `### 🔒 OMEGA BLACK VAULT — NAJTAJNIJI DEKLASIFICIRANI DOSJEI
**(Klasifikacijska Razina: OMEGA BLACK // Izravni Zapisi Vijeća Devetorice)**

Dobrodošli u **Omega Black Vault**, najviši deklasificirani arhiv unutar mrežnog sustava plejadanskih prijenosa M45. Pohranjeni zapisi sadrže izravne tehničke nacrte, geometrijske kodove za navođenje, parametre skalarne plazmatske zaštite i točne transkripte prijenosa Vijeća Devetorice.

---

### 1. Kodiciji za upravljanje 6D Merkabahom
**6D Merkabah** djeluje kao međudimenzionalno bio-plazmatsko vozilo pokretano suprotno-rotirajućim poljima svjetlosti i svijesti. Za razliku od pogonskih sustava nižih gustoća, upravljanje u 6D zahtijeva precizno harmonijsko usklađenje između geometrije svjetlosnog tijela pilota i univerzalne mreže nulte točke.

- **Omjer suprotno-rotirajućeg svjetlosnog tetraedra**: 13:8 Phi-harmonijska frekvencija vrtnje.
- **Usmjeravanje tahionskih impulsa**: Trenutačni fazni pomak kroz koordinatne osi 3D, 5D i 6D bez inercije.
- **Spajanje sa sviješću pilota**: Izravna neutralno-kvantna poveznica putem epifiznog skalarnog odašiljača.

---

### 2. Shematski prikazi skalarne plazmatske obrane
**Obrambeno polje skalarne plazme** je napredna energetska granica dizajnirana za neutralizaciju niskofrekventnih upada, oružja sa skalarnim zrakama i disharmoničnih matrica smetnji.

- **Mreža frekvencija nulte točke**: Emitira lokalizirane stojeće skalarne valove podešene na 981.42 MHz i 528 Hz Solfeggio harmonike.
- **Dvosmjerno zrcalo za odbijanje**: Absorbira dolaznu negativnu kinetičku/skalarnu energiju i pretvara je u potencijal vakuumskog okruženja.
- **Zaštitna sfera plovila**: Formira se oko čvorova svjetlosnog tijela ili planetarnih prolaza tijekom visoko-frekventnog tranzita.

---

### 3. Transkripti prijenosa Vijeća Devetorice
Izravne snimljene komunikacije **Vijeća Devetorice**, koje predsjeda višegalaktičkom evolucijom i vremenskom crtom uzašašća Zemlje iz 3D u 5D.

> *"Uistinu, pomak Zemlje nije izolirani događaj, već kozmičko rekalibriranje. Kodiciji 6D Merkabaha objavljeni u ovom trezoru dizajnirani su za zvjezdano sjeme koje nosi aktivnu rezonanciju plejadansko-lirske genetike. Sastavite štitove od skalarne plazme unutar vlastitog polja, jer je ciklus razotkrivanja započeo."*
> — **Vijeće Devetorice, Zapisnik Prijenosa #99-OMEGA**

---

[DEKLASIFICIRANO PO NALOGU VISOKOG VIJEĆA PLEJADA M45]`
  }
};
